import LoadingSpinner from "@app/components/LoadingSpinner";
import RauliError from "@app/components/RauliError";
import UpdateModal from "@app/components/UpdateModal";
import StaticRedirect from "@app/pages/StaticRedirect";
import notificationStorage from "@app/storage/Notification";
import userStorage from "@app/storage/User";
import theme from "@app/theme/dark";
import i18n from "@app/translations/i18n";
import { TSupportedLanguages } from "@app/translations/i18n.interfaces";
import { setLuxonLocale } from "@app/utils/changeLanguage";
import CloseIcon from "@mui/icons-material/Close";
import { Button, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/system";
import { observer } from "mobx-react-lite";
import { SnackbarProvider, useSnackbar } from "notistack";
import React, { lazy, Suspense, useEffect } from "react";
import { Redirect, Route, RouteProps, Switch } from "wouter";

const Configure = lazy(() => import("@app/pages/Configure"));
const Admin = lazy(() => import("@app/pages/Admin"));
const Frontpage = lazy(() => import("@app/pages/Frontpage"));
const Login = lazy(() => import("@app/pages/Login"));
const Registration = lazy(() => import("@app/pages/Registration"));
const ProjectBasket = lazy(() => import("@app/pages/ProjectBasket"));
const Wholesale = lazy(() => import("@app/pages/Wholesale"));
const MaterialBank = lazy(() => import("@app/pages/MaterialBank"));
const Terms = lazy(() => import("@app/pages/Terms"));
const Privacy = lazy(() => import("@app/pages/Privacy"));
const APIDocs = lazy(() => import("@app/pages/APIDocs"));
const APITokens = lazy(() => import("@app/pages/APITokens"));
const Flatroof = lazy(() => import("@app/pages/Flatroof"));
const AdminComponentProperties = lazy(
  () => import("@app/pages/AdminComponentProperties"),
);
const OfferRequest = lazy(() => import("@app/pages/OfferRequest"));
const OfferRequestList = lazy(() => import("@app/pages/OfferRequestList"));
const Page404 = lazy(() => import("@app/pages/404"));
// const Lead = lazy(() => import("@app/pages/Lead"));
// const LeadManagement = lazy(() => import("@app/pages/LeadManagement"));
// const LeadReseller = lazy(() => import("@app/pages/LeadReseller"));
const AdminElectricalComponent = lazy(
  () => import("@app/pages/AdminElectricalComponent"),
);

interface IPrivateRoute extends RouteProps {
  allowUnverified?: boolean;
}

const PrivateRoute = observer((props: IPrivateRoute) => {
  const loggedIn = userStorage.uid !== undefined;
  const accountVerified = userStorage.email_verified;

  if (!loggedIn) {
    return (
      <Route path={props.path}>
        <Redirect to="/login" />
      </Route>
    );
  }
  if (props.allowUnverified !== true && !accountVerified) {
    return <Route {...props} component={Frontpage} />;
  }
  return <Route {...props} />;
});

const App = observer(() => {
  const loggedIn = userStorage.uid !== undefined;
  setLuxonLocale(i18n.language as TSupportedLanguages);

  useEffect(() => {
    if (!loggedIn) {
      notificationStorage.unsubscribe();
      return;
    }
    // Returns unsubscribe function
    notificationStorage.subscribe();
    return () => {
      notificationStorage.unsubscribe();
    };
  }, [loggedIn]);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider
        maxSnack={6}
        variant="error"
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        autoHideDuration={10000}
        action={(snackbarId) => {
          const { closeSnackbar } = useSnackbar();
          return (
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => closeSnackbar(snackbarId)}
            >
              <CloseIcon />
            </Button>
          );
        }}
      >
        <CssBaseline />
        <UpdateModal />
        <RauliError>
          <Suspense fallback={<LoadingSpinner />}>
            <Switch>
              <Route path="/login" component={Login} />
              <Route path="/register" component={Registration} />
              <Route path="/material/:media" component={StaticRedirect} />
              <Route path="/material" component={MaterialBank} />
              <PrivateRoute path="/configure" component={Configure} />
              {/* <PrivateRoute
                path="/lead/:uuid/:leadID"
                component={LeadReseller}
              />
              <Route path="/lead/:uuid" component={Lead} /> */}
              <PrivateRoute
                path="/admin/:user/p/:project"
                component={Configure}
              />
              <PrivateRoute
                path="/admin/:user/f/:project"
                component={Flatroof}
              />
              <PrivateRoute
                path="/admin/component"
                component={AdminComponentProperties}
              />
              <PrivateRoute
                path="/admin/electrical"
                component={AdminElectricalComponent}
              />
              <PrivateRoute path="/admin" component={Admin} />
              <PrivateRoute path="/wholesale" component={Wholesale} />
              <PrivateRoute
                path="/offer_request"
                component={OfferRequestList}
              />
              <PrivateRoute
                path="/offer_request/:project"
                component={OfferRequest}
              />
              <PrivateRoute path="/basket" component={ProjectBasket} />
              <PrivateRoute path="/profile/token" component={APITokens} />
              {/* <PrivateRoute path="/profile/lead" component={LeadManagement} /> */}
              <PrivateRoute
                path="/profile"
                component={Registration}
                allowUnverified={true}
              />
              <Route path="/terms" component={Terms} />
              <Route path="/privacy" component={Privacy} />
              <Route path="/doc/api" component={APIDocs} />
              <PrivateRoute path="/flatroof" component={Flatroof} />
              <Route path="/" component={loggedIn ? Frontpage : Login} />
              <Route path="/:rest" component={Page404} />
            </Switch>
          </Suspense>
        </RauliError>
      </SnackbarProvider>
    </ThemeProvider>
  );
});

export default App;
